/**
 * ASCII Art Generator - 动画控制器
 *
 * 统一管理动画循环、波浪效果、鼠标交互，为引擎提供动画上下文
 * Wave 8.2: 集成性能监控
 */

import { AnimationLoop } from '../animation/AnimationLoop'
import { WaveEffect, createWaveConfigFromSettings } from '../animation/WaveEffect'
import { MouseTracker } from '../interaction/MouseTracker'
import { isPerfMonitorEnabled } from '../performance/features'
import type { AnimationContext, AnimationFrame } from '../animation/types'
import type { MouseState } from '../interaction/types'
import type { FullRenderSettings, MousePosition } from '../types'
import type { DegradeLevel, PerformanceMonitorState } from '../export/runtime/performanceMonitor'

/**
 * 动画控制器配置
 */
export interface AnimationControllerOptions {
  /** 目标帧率 */
  targetFPS?: number
  /** 动画启用状态 */
  enabled?: boolean
  /** 帧回调函数 */
  onFrame?: (context: AnimationContext) => void
  /** Wave 8.2: 性能降级回调 */
  onDegrade?: (level: DegradeLevel) => void
  /** Wave 8.2: 性能恢复回调 */
  onRecover?: () => void
}

/**
 * 动画控制器
 *
 * 统一协调：
 * - AnimationLoop: 帧循环控制
 * - WaveEffect: 波浪动画计算
 * - MouseTracker: 鼠标位置追踪
 */
export class AnimationController {
  private animationLoop: AnimationLoop
  private waveEffect: WaveEffect
  private mouseTracker: MouseTracker

  private enabled: boolean
  private time: number = 0
  private frameCount: number = 0

  private onFrame?: (context: AnimationContext) => void
  // Wave 8.2: 性能监控回调
  private onDegrade?: (level: DegradeLevel) => void
  private onRecover?: () => void
  // Wave 8.2: 当前降级级别
  private currentDegradeLevel: DegradeLevel = 'none'

  constructor(options: AnimationControllerOptions = {}) {
    this.enabled = options.enabled ?? true
    this.targetFPS = options.targetFPS ?? 60
    this.onFrame = options.onFrame
    this.onDegrade = options.onDegrade
    this.onRecover = options.onRecover

    // 初始化组件
    this.waveEffect = new WaveEffect({
      amplitude: 0,
      frequency: 3,
      speed: 0.05,
      direction: 'both'
    })

    this.mouseTracker = new MouseTracker({ enableTouch: true })

    // 创建动画循环
    this.animationLoop = new AnimationLoop({
      targetFPS: this.targetFPS,
      onFrame: this.handleFrame,
      onPause: () => {
        // 可选：暂停时的处理
      },
      onResume: () => {
        // 可选：恢复时的处理
      }
    })

    // Wave 8.2: 设置性能监控回调（如果启用）
    if (isPerfMonitorEnabled()) {
      this.animationLoop.setDegradeCallback(this.handleDegrade)
      this.animationLoop.setRecoverCallback(this.handleRecover)
    }
  }

  private targetFPS: number

  /**
   * 绑定到 Canvas 元素
   */
  attachToCanvas(canvas: HTMLCanvasElement): void {
    this.mouseTracker.attachToCanvas(canvas)
  }

  /**
   * 解除 Canvas 绑定
   */
  detachFromCanvas(): void {
    this.mouseTracker.detach()
  }

  /**
   * 启动动画
   */
  start(): void {
    if (!this.enabled) return
    this.animationLoop.start()
  }

  /**
   * 停止动画
   */
  stop(): void {
    this.animationLoop.stop()
  }

  /**
   * 切换动画状态
   */
  toggle(): void {
    if (this.animationLoop.isRunning()) {
      this.stop()
    } else {
      this.start()
    }
  }

  /**
   * 设置动画启用状态
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled
    if (enabled) {
      this.start()
    } else {
      this.stop()
    }
  }

  /**
   * 检查动画是否启用
   */
  isEnabled(): boolean {
    return this.enabled
  }

  /**
   * 检查动画是否正在运行
   */
  isRunning(): boolean {
    return this.animationLoop.isRunning()
  }

  /**
   * 更新波浪参数
   */
  updateWaveSettings(settings: FullRenderSettings): void {
    const config = createWaveConfigFromSettings(
      settings.ditherWaveAmplitude,
      settings.ditherWaveFrequency,
      settings.ditherWaveSpeed
    )
    this.waveEffect.setConfig(config)
  }

  /**
   * 更新鼠标交互设置
   */
  updateMouseSettings(_settings: FullRenderSettings): void {
    // MouseTracker 本身不需要额外设置
    // 交互计算在引擎中使用 settings 直接计算
  }

  /**
   * 获取当前动画上下文
   */
  getContext(): AnimationContext {
    const mouseState = this.mouseTracker.getState()
    const mousePosition: MousePosition = {
      x: mouseState.x,
      y: mouseState.y,
      isInCanvas: mouseState.isInCanvas
    }

    return {
      time: this.time,
      deltaTime: 0,
      frameCount: this.frameCount,
      mouseState: mousePosition
    }
  }

  /**
   * 获取波浪偏移量
   */
  getWaveOffset(x: number, y: number): { offsetX: number; offsetY: number } {
    return this.waveEffect.getOffset(x, y)
  }

  /**
   * 获取鼠标状态
   */
  getMouseState(): MouseState {
    return this.mouseTracker.getState()
  }

  /**
   * 获取当前时间
   */
  getTime(): number {
    return this.time
  }

  /**
   * 设置目标帧率
   */
  setTargetFPS(fps: number): void {
    this.targetFPS = fps
    this.animationLoop.setTargetFPS(fps)
  }

  /**
   * 获取目标帧率
   */
  getTargetFPS(): number {
    return this.targetFPS
  }

  /**
   * 重置动画状态
   */
  reset(): void {
    this.time = 0
    this.frameCount = 0
    this.waveEffect.reset()
    this.mouseTracker.reset()
    // Wave 8.2: 重置性能监控
    this.currentDegradeLevel = 'none'
    this.animationLoop.resetPerformanceMonitor()
  }

  /**
   * 设置帧回调函数
   */
  setOnFrame(callback: (context: AnimationContext) => void): void {
    this.onFrame = callback
  }

  /**
   * 内部帧处理函数
   */
  private handleFrame = (frame: AnimationFrame): void => {
    // 更新时间（转换为秒）
    const deltaTimeSeconds = frame.deltaTime / 1000
    this.time += deltaTimeSeconds
    this.frameCount = frame.frameCount

    // 更新波浪效果
    this.waveEffect.update(deltaTimeSeconds)

    // 创建动画上下文
    const context = this.getContext()
    context.deltaTime = deltaTimeSeconds

    // 调用用户回调
    if (this.onFrame) {
      this.onFrame(context)
    }
  }

  // ============================================
  // Wave 8.2: 性能监控相关方法
  // ============================================

  /**
   * 处理性能降级
   */
  private handleDegrade = (level: DegradeLevel): void => {
    this.currentDegradeLevel = level
    console.warn(`[Performance] Degrade to level: ${level}`)
    if (this.onDegrade) {
      this.onDegrade(level)
    }
  }

  /**
   * 处理性能恢复
   */
  private handleRecover = (): void => {
    // 恢复到上一级
    if (this.currentDegradeLevel === 'heavy') {
      this.currentDegradeLevel = 'medium'
    } else if (this.currentDegradeLevel === 'medium') {
      this.currentDegradeLevel = 'light'
    } else if (this.currentDegradeLevel === 'light') {
      this.currentDegradeLevel = 'none'
    }
    console.info(`[Performance] Recover to level: ${this.currentDegradeLevel}`)
    if (this.onRecover) {
      this.onRecover()
    }
  }

  /**
   * 设置性能降级回调
   */
  setOnDegrade(callback: (level: DegradeLevel) => void): void {
    this.onDegrade = callback
  }

  /**
   * 设置性能恢复回调
   */
  setOnRecover(callback: () => void): void {
    this.onRecover = callback
  }

  /**
   * 获取当前FPS
   */
  getCurrentFPS(): number {
    return this.animationLoop.getCurrentFPS()
  }

  /**
   * 获取平均FPS
   */
  getAverageFPS(): number {
    return this.animationLoop.getAverageFPS()
  }

  /**
   * 获取当前降级级别
   */
  getDegradeLevel(): DegradeLevel {
    return this.animationLoop.getDegradeLevel()
  }

  /**
   * 获取性能监控状态
   */
  getPerformanceState(): PerformanceMonitorState | null {
    return this.animationLoop.getPerformanceState()
  }

  /**
   * 是否正在降级
   */
  isDegrading(): boolean {
    return this.animationLoop.isDegrading()
  }

  /**
   * 获取建议的目标FPS
   */
  getSuggestedTargetFPS(): number {
    return this.animationLoop.getSuggestedTargetFPS()
  }

  /**
   * 手动设置降级级别
   */
  setDegradeLevel(level: DegradeLevel): void {
    this.currentDegradeLevel = level
    this.animationLoop.setDegradeLevel(level)
  }

  /**
   * 销毁控制器
   */
  destroy(): void {
    this.animationLoop.destroy()
    this.mouseTracker.destroy()
  }
}

/**
 * 创建动画控制器的工厂函数
 */
export function createAnimationController(
  options?: AnimationControllerOptions
): AnimationController {
  return new AnimationController(options)
}